import { useEffect, useState } from 'react'
import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE } from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { SensorProduct, LINE_COLORS, DeviceInfo, UnitCategoryId } from '../../common/enums'
import { useAccountContext } from '../../contexts'
import { convertBaseToDisplay } from '../../common/conversions'
import CanvasJSReact from '@canvasjs/react-charts'
import { Viewport } from '../../common/types'
const { CanvasJSChart } = CanvasJSReact

interface Props {
  device: DeviceInfo
  sensorType: SensorProduct
  sensorReadings: any[]
  viewport: Viewport
  isLoading: boolean
  zoomEnabled?: boolean
}

export function UnifiedChartVegetronixTHERM200({
  device,
  sensorType,
  sensorReadings,
  viewport,
  isLoading,
  zoomEnabled = true,
}: Props) {
  const { userUnitSettings, unitConversions } = useAccountContext()
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  const unitCategoryId = UnitCategoryId.SoilTemperature
  const unitId = userUnitSettings[unitCategoryId].unitId
  const units = unitConversions[unitCategoryId].abbreviations[unitId]
  const formula = unitConversions[unitCategoryId].formulas[unitId]
  const precision = userUnitSettings[unitCategoryId].precision

  useEffect(() => {
    const visible: Record<string, boolean> = {}

    Object.keys(sensorReadings)
      .filter((key: string) => !key.includes('T0'))
      .map((key: string) => {
        const sensorName = sensorReadings[Number(key)].metadata.sensorName
        visible[sensorName] = true
        return null
      })

    setVisible(visible)
  }, [sensorReadings])

  const dataPoints: Record<string, any> = {}
  let yAxisMinimum = 100
  let yAxisMaximum = -100
  let trackDay = -1

  Object.keys(sensorReadings).forEach((key: any) => {
    const sensor = sensorReadings[key]

    const sensorName = sensor.metadata.sensorName

    const data = Object.keys(sensor.readings)
      .filter((key: string) => {
        const timestamp = parseInt(key) * 1000
        return timestamp >= viewport.minimum && timestamp <= viewport.maximum
      })
      .map((key: string) => {
        const timestamp = parseInt(key)
        const reading = convertBaseToDisplay(sensor.readings[timestamp] as number, formula)

        yAxisMinimum = Math.min(yAxisMinimum, reading)
        yAxisMaximum = Math.max(yAxisMaximum, reading)

        return {
          label: convertTimestamp(timestamp),
          x: timestamp * 1000,
          y: roundToDecimal(reading, precision),
        }
      })

    dataPoints[sensorName] = data
  })

  const lines =
    Object.keys(dataPoints).length > 0
      ? Object.keys(dataPoints).map((sensorPort: string, index: number) => {
          const points = dataPoints[sensorPort]
          return {
            name: `${sensorPort}`,
            type: 'line',
            showInLegend: true,
            xValueType: 'dateTime',
            color: LINE_COLORS[sensorPort],
            visible: visible[sensorPort],
            dataPoints: points,
          }
        })
      : []

  const xAxisMinimum = dataPoints[Object.keys(dataPoints)[0]]?.[0]?.x || viewport.minimum
  const xAxisMaximum =
    dataPoints[Object.keys(dataPoints)[0]]?.[dataPoints[Object.keys(dataPoints)[0]].length - 1]?.x || viewport.maximum

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilTemperature`,
    title: {
      text: 'Soil Temperature',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`
        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y)} ${units} </strong><br/>`
        }
        return content
      },
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: xAxisMinimum,
      viewportMaximum: xAxisMaximum,
    },
    axisY: {
      includeZero: false,
      maximum: yAxisMaximum > 0 ? yAxisMaximum * 1.2 : yAxisMaximum * 0.8,
      minimum: yAxisMinimum > 0 ? yAxisMinimum * 0.8 : yAxisMinimum * 1.2,
      title: `Temperature (${units})`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
    },
    data: [...lines],
    legend: {
      itemclick: (e: any) => {
        setVisible((prev) => ({ ...prev, [e.dataSeries.name]: !prev[e.dataSeries.name] }))
      },
    },
  }

  const data = Object.keys(dataPoints).map((sensorPort: string) => {
    const readings = dataPoints[sensorPort]
    const lastIndex = Object.keys(readings).length - 1
    const lastTimestamp = readings[lastIndex] ? new Date(Number(readings[lastIndex]?.x)).toLocaleString() : '...'
    const lastReading = readings[lastIndex] ? Number(readings[lastIndex]?.y).toFixed(1) : ''

    return [`Port ${sensorPort}: ${sensorType}`, `${lastTimestamp}`, `${lastReading} ${units}`]
  })

  return (
    <CollapsibleSection title="Soil Temperature" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <CanvasJSChart options={options} />
    </CollapsibleSection>
  )
}
